<template>
  <auth-guard
    class="pb-8"
    route
  >
    <app-loading-indicator v-if="loading" />
    <ValidationObserver
      v-else
      v-slot="{ invalid }"
      ref="validationObserver"
    >
      <base-title :title="pageTitle" />
      <v-sheet class="pa-4">
        <v-alert
          v-if="parentLot"
          type="info"
          dense
          outlined
          class="mb-4"
        >
          Creating a sub-lot under primary lot <strong>{{ parentLot.name }}</strong> (copied configuration). Enter a unique name to create the sub-lot.
        </v-alert>
        <v-alert
          v-else-if="templateLotName"
          type="info"
          dense
          outlined
          class="mb-4"
        >
          Copied configuration from lot <strong>{{ templateLotName }}</strong>. Enter a new name to create the lot.
        </v-alert>
        <v-form>
          <v-row>
            <v-col
              cols="12"
              md="6"
            >
              <ValidationProvider
                rules="required|max:55"
                v-slot="{ errors }"
              >
                <v-text-field
                  ref="lotNameInput"
                  label="Name*"
                  v-model="name"
                  hint="exp. 12345 (HOUSE)"
                  counter="55"
                  :error-messages="nameConflictError ? [...errors, nameConflictError] : errors"
                  autofocus
                />
              </ValidationProvider>
              <div
                v-if="!parentLot"
                class="mb-4"
              >
                <div
                  v-if="subLots.length"
                  class="mb-2"
                >
                  <div class="text-caption grey--text text--darken-1 mb-1">
                    Sub-lots to create ({{ subLots.length }}):
                  </div>
                  <v-chip
                    v-for="(subLotName, idx) in subLots"
                    :key="idx"
                    close
                    small
                    color="primary"
                    outlined
                    class="mr-2 mb-1"
                    @click:close="removeSubLot(idx)"
                  >
                    {{ subLotName }}
                  </v-chip>
                </div>
                <v-btn
                  small
                  outlined
                  color="primary"
                  @click="openSubLotDialog"
                >
                  <v-icon
                    small
                    left
                  >
                    mdi-source-branch-plus
                  </v-icon>
                  Add Sublot
                </v-btn>
              </div>
              <ValidationProvider
                rules="max:20"
                v-slot="{ errors }"
              >
                <v-text-field
                  label="Suffix"
                  v-model="suffix"
                  hint="rarely used exp. 1234"
                  counter="20"
                  :error-messages="errors"
                />
              </ValidationProvider>
              <v-radio-group
                label="Lot Type"
                v-model="isNonConverter"
              >
                <v-radio
                  label="Converter"
                  :value="false"
                />
                <v-radio
                  label="Non-Converter"
                  :value="true"
                />
              </v-radio-group>
            </v-col>
          </v-row>
          <v-row>
            <v-col
              cols="12"
              sm="6"
            >
              <ValidationProvider
                rules="required"
                v-slot="{ errors }"
              >
                <v-radio-group
                  label="Entity*"
                  v-model="entityUrl"
                  :error-messages="errors"
                >
                  <v-radio
                    v-for="et in entities"
                    :key="et.name"
                    :label="et.name"
                    :value="et.url"
                  />
                </v-radio-group>
              </ValidationProvider>
            </v-col>
            <v-col
              cols="12"
              md="6"
            >
              <ValidationProvider
                rules="required"
                v-slot="{ errors }"
              >
                <v-radio-group
                  label="Designation*"
                  v-model="lotType"
                  :error-messages="errors"
                >
                  <v-radio
                    v-for="lt in lotTypes"
                    :key="lt.value"
                    :label="lt.text + (lt['expects_settlement'] ? '*' : '')"
                    :value="lt.value"
                  />
                  <span class="text-caption">*Yard Required</span>
                </v-radio-group>
              </ValidationProvider>
            </v-col>
            <v-col
              cols="12"
              md="6"
            >
              <ValidationProvider
                rules="required"
                v-slot="{ errors }"
              >
                <v-radio-group
                  label="Division*"
                  v-model="division"
                  :error-messages="errors"
                >
                  <v-radio
                    v-for="division in divisions"
                    :key="division.id"
                    :label="division.name"
                    :value="division.id"
                  />
                </v-radio-group>

                <ValidationProvider
                  rules="required"
                  v-slot="{ errors }"
                >
                  <v-radio-group
                    label="Processing Method*"
                    v-model="processingMethod"
                    :error-messages="errors"
                  >
                    <v-radio
                      v-for="lmt in processingMethods"
                      :key="lmt.text"
                      :label="lmt.text"
                      :value="lmt.value"
                    />
                  </v-radio-group>
                </ValidationProvider>
              </ValidationProvider>
            </v-col>
            <v-col
              cols="12"
              md="6"
            >
              <v-radio-group
                label="Material Type"
                v-model="materialType"
              >
                <v-radio
                  label="(None)"
                  value="none"
                />
                <v-radio
                  v-for="lmt in lotMaterialTypes"
                  :key="lmt.text"
                  :label="lmt.text"
                  :value="lmt.value"
                />
              </v-radio-group>
            </v-col>
            <v-col
              cols="12"
              md="6"
            >
              <v-radio-group
                label="Region"
                v-model="region"
              >
                <v-radio
                  label="(None)"
                  :value="null"
                />
                <v-radio
                  v-for="reg in regions"
                  :key="reg.id"
                  :label="reg.name"
                  :value="reg.id"
                />
              </v-radio-group>
            </v-col>
          </v-row>
          <v-row>
            <v-col
              cols="12"
              md="6"
            >
              <ValidationProvider
                v-slot="{ errors }"
              >
                <search-autocomplete
                  v-model="accountManager"
                  label="Account Manager"
                  item-text="username"
                  api-path="/api/users/"
                  :params="{ fields: 'username,url' }"
                  :error-messages="errors"
                  :format-entry="x => Object.assign({ friendlyName: x.username }, x)"
                />
              </ValidationProvider>
            </v-col>
          </v-row>
          <v-row>
            <v-col
              cols="12"
              md="6"
            >
              <ValidationProvider
                rules="required"
                v-slot="{ errors }"
              >
                <search-autocomplete
                  v-model="ppmCalc"
                  label="PPM Calculator*"
                  api-path="/api/lots/ppm_calculation_search/"
                  :format-entry="x => Object.assign({}, x, { friendlyName: x.name })"
                  :error-messages="errors"
                />
              </ValidationProvider>
            </v-col>
          </v-row>
          <v-row>
            <v-col
              cols="12"
              md="6"
            >
              <search-autocomplete
                v-model="smelter"
                label="Smelter"
                api-path="/api/lots/smelter_search/"
                :format-entry="x => Object.assign({}, x, { friendlyName: x.name })"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col
              cols="12"
              md="6"
            >
              <ValidationProvider
                :rules="yardRequired ? 'required' : null"
                v-slot="{ errors }"
              >
                <search-autocomplete
                  v-model="chipYards"
                  :label="yardRequired ? 'Select a Yard*' : 'Select a Yard'"
                  api-path="/api/lots/yard_search/"
                  :params="{ status: 'GS' }"
                  multiple
                  :format-entry="x => Object.assign({}, x, { friendlyName: `${x.id} - ${x.name} | ${x.full_address || ''} | ${x.status}${x.brokerageterm ? ' (Has Terms)' : ''}` })"
                />
                <div
                  class="red--text"
                  v-for="(err, index) in errors"
                  :key="index"
                >
                  <small>
                    {{ err }}
                  </small>
                </div>
                <v-alert
                  v-if="yardsWithoutBrokerageTerms.length > 0 && lotType !== 'DR'"
                  type="warning"
                  dense
                  outlined
                  class="mt-2"
                >
                  <strong>Warning:</strong> The following yard(s) do not have brokerage terms:
                  <ul class="mt-1 mb-0">
                    <li
                      v-for="yard in yardsWithoutBrokerageTerms"
                      :key="yard.id"
                    >
                      {{ yard.name }}
                    </li>
                  </ul>
                  <v-checkbox
                    v-model="acknowledgedMissingTerms"
                    label="I acknowledge that the selected yard(s) do not have brokerage terms"
                    class="mt-2"
                    hide-details
                  />
                </v-alert>
              </ValidationProvider>
            </v-col>
          </v-row>
          <v-row>
            <v-col
              cols="12"
              md="6"
            >
              <search-autocomplete
                v-model="designatedHedges"
                label="Designated Hedge(s)"
                api-path="/api/lots/hedge_search/"
                item-text="friendlyName"
                multiple
                return-object
                :format-entry="x => Object.assign({}, x, { friendlyName: String(x.id) })"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col
              cols="12"
              md="6"
            >
              <date-input
                v-model="dueDate"
                label="Lot Due Date"
                hint="Defaults to creation date + 60 days"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-textarea
                name="notes"
                label="Notes"
                v-model="notes"
                counter
                :rows="4"
                auto-grow
              />
            </v-col>
          </v-row>
          <text-fab
            :disabled="invalid || creatingLot || !canCreateLot"
            bottom
            right
            fixed
            icon-text="mdi-invoice-text-plus"
            color="primary"
            @click="create"
          >
            Create
          </text-fab>
        </v-form>
        <edit-dialog
          v-model="subLotDialog"
          headline-text="Add Sublot"
          accept-text="Add"
          :max-width="450"
          :inputs-valid="isSubLotNameValid"
          @accept="addSubLot"
        >
          <v-text-field
            ref="subLotNameInput"
            v-model="newSubLotName"
            label="Sublot Name*"
            hint="Must be different from primary lot and other sublots"
            counter="55"
            :error-messages="subLotNameErrors"
            autofocus
            @keydown.enter.prevent="onSubLotEnter"
          />
        </edit-dialog>
      </v-sheet>
    </ValidationObserver>
  </auth-guard>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import axios from 'axios'
import { fetchEntities } from '@/requests/entities'
import lot from '@/views/Lots/Lot.vue'
import { fetchAllDivisions } from '@/requests/divisions'
import SearchAutocomplete from '@/base/components/SearchAutocomplete.vue'

export default {
  name: 'CreateLot',
  components: { SearchAutocomplete },
  computed: {
    lot () {
      return lot
    },
    ...mapGetters({
      lotTypes: 'lotStore/lotTypesActive'
    }),
    yardRequired () {
      for (const lt of this.lotTypes) {
        if (this.lotType === lt.value && lt['expects_settlement']) {
          return true
        }
      }
      return false
    },
    lotMaterialTypes () {
      return this.$store.getters['getLotTypes']
    },
    processingMethods () {
      return this.$store.getters['getLotProcessingMethods'].filter(item => item.value !== 'NA')
    },
    yardsWithoutBrokerageTerms () {
      if (!this.chipYards || this.chipYards.length === 0) {
        return []
      }
      return this.chipYards.filter(yard => !yard.brokerageterm || yard.brokerageterm === null)
    },
    pageTitle () {
      if (this.parentLot) {
        return `Create Sublot (Primary: ${this.parentLot.name})`
      }
      if (this.templateLotName) {
        return `Create Lot (Template: ${this.templateLotName})`
      }
      return 'Create Lot'
    },
    nameConflictError () {
      const trimmed = (this.name || '').trim().toLowerCase()
      if (!trimmed) return ''
      if (this.parentLot) {
        if (this.parentLot.name && this.parentLot.name.trim().toLowerCase() === trimmed) {
          return 'Sublot name cannot be the same as the primary lot name'
        }
        if ((this.parentLot.children || []).some(c => c.name && c.name.trim().toLowerCase() === trimmed)) {
          return 'Sublot name cannot be the same as another sublot on this primary lot'
        }
      } else if (this.subLots.some(s => s.trim().toLowerCase() === trimmed)) {
        return 'Primary lot name cannot be the same as a queued sublot name'
      }
      return ''
    },
    subLotNameErrors () {
      const trimmed = (this.newSubLotName || '').trim()
      if (!trimmed) return []
      if (trimmed.length > 55) {
        return ['Sublot name cannot exceed 55 characters']
      }
      const lower = trimmed.toLowerCase()
      if (this.name && this.name.trim().toLowerCase() === lower) {
        return ['Sublot name cannot be the same as the primary lot name']
      }
      if (this.subLots.some(s => s.trim().toLowerCase() === lower)) {
        return ['Sublot name cannot be the same as another sublot']
      }
      return []
    },
    isSubLotNameValid () {
      const trimmed = (this.newSubLotName || '').trim()
      return trimmed.length > 0 && trimmed.length <= 55 && this.subLotNameErrors.length === 0
    },
    canCreateLot () {
      if (this.nameConflictError) {
        return false
      }
      // If lot type is 'DR' (direct), no acknowledgment needed
      if (this.lotType === 'DR') {
        return true
      }
      // If there are yards without brokerage terms, user must acknowledge
      if (this.yardsWithoutBrokerageTerms.length > 0) {
        return this.acknowledgedMissingTerms
      }
      return true
    }
  },
  data: () => ({
    templateLotName: null,
    parentLot: null,
    subLots: [],
    subLotDialog: false,
    newSubLotName: '',
    accountManager: null,
    loading: false,
    name: null,
    entityUrl: null,
    entities: [],
    suffix: null,
    smelter: null,
    division: null,
    notes: null,
    ppmCalc: null,
    creatingLot: false,
    lotType: null,
    materialType: 'none',
    designatedHedges: [],
    chipYards: [],
    timeout: null,
    selectableYards: [],
    search: null,
    divisions: [],
    processingMethod: null,
    acknowledgedMissingTerms: false,
    region: null,
    regions: [],
    isNonConverter: false,
    dueDate: new Date(Date.now() + 60 * 24 * 60 * 60 * 1000).toISOString().substr(0, 10)
  }),
  watch: {
    search (val) {
      if (!val) {
        this.selectableYards = []
        return
      }
      this.fetchResults(val)
    }
  },
  methods: {
    formatYardResult (yard) {
      return Object.assign({
        friendlyName: `${yard.name} (${yard.city || '--'}, ${yard.state || '--'})`
      }, yard)
    },
    ...mapActions({
      createLot: 'lotStore/createLot',
      updateLot: 'lotStore/updateLot'
    }),
    focusInputAtEnd (refName) {
      this.$nextTick(() => {
        setTimeout(() => {
          const field = this.$refs[refName]
          if (field) {
            if (typeof field.focus === 'function') field.focus()
            const inputEl = field.$el ? field.$el.querySelector('input') : null
            if (inputEl && typeof inputEl.setSelectionRange === 'function') {
              const len = (inputEl.value || '').length
              inputEl.setSelectionRange(len, len)
            }
          }
        }, 50)
      })
    },
    openSubLotDialog () {
      this.newSubLotName = this.name || ''
      this.subLotDialog = true
      this.focusInputAtEnd('subLotNameInput')
    },
    addSubLot () {
      if (!this.isSubLotNameValid) return
      this.subLots.push(this.newSubLotName.trim())
      this.newSubLotName = ''
    },
    onSubLotEnter () {
      if (!this.isSubLotNameValid) return
      this.addSubLot()
      this.subLotDialog = false
    },
    removeSubLot (idx) {
      this.subLots.splice(idx, 1)
    },
    async applyTemplate (lotId, isParentLot = false) {
      try {
        const response = await axios.get(`/api/lots/${lotId}/`)
        const lot = response.data
        this.templateLotName = lot.name

        if (isParentLot) {
          if (lot.parent || lot.parent_data) {
            this.$store.commit('setSnackbarError', 'Cannot add a sublot to a lot that is already a sublot')
          } else {
            this.parentLot = {
              id: lot.id,
              name: lot.name,
              url: lot.url,
              children: lot.children_data || []
            }
            this.name = lot.name || ''
          }
        }

        if (lot.entity) this.entityUrl = lot.entity
        if (lot.designation) this.lotType = lot.designation

        // Match division by id, object, URL, or division_name
        if (lot.inventory?.division?.id) {
          this.division = lot.inventory.division.id
        } else if (lot.division_id) {
          this.division = lot.division_id
        } else if (typeof lot.division === 'number') {
          this.division = lot.division
        } else if (lot.division?.id) {
          this.division = lot.division.id
        } else if (typeof lot.division === 'string' && lot.division.includes('/')) {
          const match = lot.division.match(/\/(\d+)\/?$/)
          if (match) this.division = parseInt(match[1])
        } else if (lot.division_name && this.divisions.length) {
          const matchDiv = this.divisions.find(d => d.name === lot.division_name)
          if (matchDiv) this.division = matchDiv.id
        }

        if (lot.processing_method) this.processingMethod = lot.processing_method
        if (lot.lot_type) this.materialType = lot.lot_type
        if (typeof lot.is_nonconverter !== 'undefined') this.isNonConverter = Boolean(lot.is_nonconverter)

        // Match region by id, object, URL, or region_name
        if (lot.region_id) {
          this.region = lot.region_id
        } else if (typeof lot.region === 'number') {
          this.region = lot.region
        } else if (lot.region?.id) {
          this.region = lot.region.id
        } else if (typeof lot.region === 'string' && lot.region.includes('/')) {
          const match = lot.region.match(/\/(\d+)\/?$/)
          if (match) this.region = parseInt(match[1])
        } else if (lot.region_name && this.regions.length) {
          const matchReg = this.regions.find(r => r.name === lot.region_name)
          if (matchReg) this.region = matchReg.id
        }

        if (lot.account_manager) {
          let amUrl = typeof lot.account_manager === 'object' ? lot.account_manager.url : lot.account_manager
          let amUsername = lot.account_manager__username || (typeof lot.account_manager === 'object' ? lot.account_manager.username : null)

          if (amUrl && !amUsername) {
            try {
              const userRes = await axios.get(amUrl)
              amUsername = userRes.data?.username
            } catch (e) {
              // fallback if user endpoint is not reachable
            }
          }

          if (amUrl) {
            this.accountManager = {
              url: amUrl,
              username: amUsername || amUrl,
              friendlyName: amUsername || amUrl
            }
          }
        }

        if (lot.ppm_calculator) {
          this.ppmCalc = typeof lot.ppm_calculator === 'object'
            ? Object.assign({ friendlyName: lot.ppm_calculator.name }, lot.ppm_calculator)
            : { id: lot.ppm_calculator, name: '', friendlyName: '' }
        } else if (lot.ppm_calculator_id) {
          this.ppmCalc = { id: lot.ppm_calculator_id, name: '', friendlyName: '' }
        }

        if (lot.smelter) {
          this.smelter = typeof lot.smelter === 'object'
            ? Object.assign({ friendlyName: lot.smelter.name }, lot.smelter)
            : { id: lot.smelter, name: '', friendlyName: '' }
        } else if (lot.smelter_id) {
          this.smelter = { id: lot.smelter_id, name: '', friendlyName: '' }
        }

        if (Array.isArray(lot.yards) && lot.yards.length) {
          this.chipYards = lot.yards.map(y => Object.assign({
            friendlyName: `${y.id} - ${y.name} | ${y.full_address || ''} | ${y.status}${y.brokerageterm ? ' (Has Terms)' : ''}`
          }, y))
        }

        if (Array.isArray(lot.designated_hedges) && lot.designated_hedges.length) {
          this.designatedHedges = lot.designated_hedges.map(h => Object.assign({}, h, {
            friendlyName: String(h.id)
          }))
        }

        if (lot.suffix) this.suffix = lot.suffix
        if (lot.notes) this.notes = lot.notes
      } catch (err) {
        this.$store.commit('setSnackbarError', `Failed to load template lot: ${err.message}`)
      }
    },
    async createSingleLot (lotName, parentUrl = null) {
      let payload = {
        name: lotName,
        designation: this.lotType,
        division_id: this.division,
        ppm_calculator_id: this.ppmCalc ? (typeof this.ppmCalc === 'object' ? this.ppmCalc.id : this.ppmCalc) : null,
        entity: this.entityUrl,
        processing_method: this.processingMethod,
        is_nonconverter: this.isNonConverter
      }
      if (this.suffix) payload.suffix = this.suffix
      if (this.smelter) {
        payload.smelter_id = typeof this.smelter === 'object' ? this.smelter.id : this.smelter
      }
      if (this.notes) payload.notes = this.notes
      if (this.materialType !== 'none') payload.lot_type = this.materialType
      if (this.accountManager) {
        payload.account_manager = typeof this.accountManager === 'object' ? this.accountManager.url : this.accountManager
      }
      if (this.region) payload.region_id = this.region
      if (this.dueDate) payload.due_date = this.dueDate

      const id = await this.createLot({ payload })
      if (!id) return null

      // API patch to set the yards, hedges, and optional parent lot after
      if (this.chipYards || this.designatedHedges || parentUrl) {
        let patchPayload = { id }
        if (this.chipYards) patchPayload.yards = this.chipYards.map(h => h.id)
        if (this.designatedHedges) {
          patchPayload.designated_hedges = this.designatedHedges.map(h => h.id)
        }
        if (parentUrl) {
          patchPayload.parent = parentUrl
        }
        await this.updateLot(patchPayload)
      }

      return id
    },
    /**
     * Create a new lot (and any queued sub-lots) given the user input
     */
    async create () {
      if (this.nameConflictError) {
        this.$store.commit('setSnackbarError', this.nameConflictError)
        return
      }
      this.creatingLot = true
      try {
        const parentUrl = this.parentLot ? this.parentLot.url : null
        const id = await this.createSingleLot(this.name, parentUrl)

        if (id && !this.parentLot && this.subLots.length > 0) {
          const primaryRes = await axios.get(`/api/lots/${id}/`)
          const primaryLotUrl = primaryRes.data.url
          for (const subLotName of this.subLots) {
            await this.createSingleLot(subLotName, primaryLotUrl)
          }
        }

        if (id) {
          this.$router.push({ name: 'lot', params: { id } })
        }
      } finally {
        this.creatingLot = false
      }
    },
    fetchResults (val) {
      if (this.timeout) clearTimeout(this.timeout)
      this.timeout = setTimeout(async () => {
        const params = { status: 'GS', search: val, fields: 'id,name,full_address' }
        const response = await axios.get('/api/yards/', { params })
        this.selectableYards = response.data.results
      }, 300)
    }
  },
  async mounted () {
    this.loading = true
    try {
      const divResponse = await fetchAllDivisions({ is_inactive: false, ordering: 'name' })
      this.divisions = divResponse.data
      const entResponse = await fetchEntities()
      if (entResponse.data) this.entities = entResponse.data
      await this.$store.dispatch('regionStore/fetchRegions', { ordering: 'name' })
      this.regions = this.$store.getters['regionStore/regions']

      const templateId = this.$route.query.parentLotId || this.$route.query.templateLotId
      if (templateId) {
        await this.applyTemplate(templateId, Boolean(this.$route.query.parentLotId))
      }
    } catch (err) {
      this.$store.commit('setSnackbarError', err.message)
    }
    this.loading = false
    this.focusInputAtEnd('lotNameInput')
  }
}
</script>
